'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { useMediaQuery } from '@/hooks/use-screen'
import { cn } from '@/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

// Radix tooltips open on hover/focus only, so on a touch screen a tap never shows them. On
// touch (no hover) Tooltip takes over the open state and TooltipTrigger toggles it on tap — for
// a purely informational trigger (an info icon, a label); a trigger that is itself a control
// (button, link, field) keeps its tap for its own action unless the Tooltip opts in with
// `tapToOpen` (e.g. a help button whose only job is the explanation). Tapping elsewhere closes
// it (TooltipContent's outside-pointer dismissal).
const TooltipTouchContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
  tapToOpen?: boolean
} | null>(null)

const INTERACTIVE_TRIGGER = 'button, a[href], input, select, textarea, [role="button"]'

function Tooltip({
  open: openProp,
  defaultOpen,
  onOpenChange,
  tapToOpen,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & { tapToOpen?: boolean }) {
  const isTouch = useMediaQuery('(hover: none)')
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange]
  )
  const touchContext = React.useMemo(
    () => ({ open, setOpen, tapToOpen }),
    [open, setOpen, tapToOpen]
  )

  if (!isTouch) {
    return (
      <TooltipPrimitive.Root
        open={openProp}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        {...props}
      />
    )
  }
  return (
    <TooltipTouchContext.Provider value={touchContext}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </TooltipTouchContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onClick, onPointerDown, onFocus, ...props }, ref) => {
  const touch = React.useContext(TooltipTouchContext)
  // Radix closes an open tooltip on the trigger's pointerdown, before the click — remember
  // whether it was open so a second tap closes it rather than reopening it.
  const wasOpenRef = React.useRef(false)
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onFocus={(event) => {
        onFocus?.(event)
        // Radix opens on any focus. A sheet, dialog or page that moves focus to its first
        // control on open (the side panel's search, the Arbitrage page) then pops that control's
        // tooltip unasked — on a phone with no way to hover it away. Only keyboard focus
        // (:focus-visible) should open it; preventDefault skips Radix's own focus handler.
        if (!event.currentTarget.matches(':focus-visible')) event.preventDefault()
      }}
      onPointerDown={(event) => {
        wasOpenRef.current = touch?.open ?? false
        onPointerDown?.(event)
      }}
      onClick={(event) => {
        onClick?.(event)
        if (!touch) return
        const isControl = event.currentTarget.matches(INTERACTIVE_TRIGGER)
        if (!(touch.tapToOpen ?? !isControl)) return
        // Also stops Radix's own click handler, which would close it again straight away.
        event.preventDefault()
        touch.setOpen(!wasOpenRef.current)
        wasOpenRef.current = !wasOpenRef.current
      }}
      {...props}
    />
  )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'menu-surface animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 origin-[--radix-tooltip-content-transform-origin] overflow-hidden px-3 py-1.5 text-sm',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
